import { range } from 'lodash-es';
import { Subject } from 'rxjs';
export const wrapSettlement = (p, wrapper) => new Promise((res, rej) => {
    p.then((x) => wrapper(() => res(x))).catch((e) => wrapper(() => rej(e)));
});
export const monotonicTime = () => performance.now(); // expand this if we want to run on Node later
export const delay = (msec) => new Promise((res) => setTimeout(res, msec));
/** Wait for a promise to resolve or reject. If it doesn't do either within `msec`, reject with 'timeout'. */
export const timeoutRace = (p, msec, exc = 'timeout') => Promise.race([p, new Promise((_res, rej) => setTimeout(() => rej(exc), msec))]);
/** Same as `timeoutRace`, except if `msec` is <= 0, don't apply a timeout */
export const timeoutRaceOptional = (p, msec, exc = 'timeout') => msec > 0 ? timeoutRace(p, msec, exc) : p;
/**
 * FairServiceScheduler manages when tasks are run using a best-effort priority system.
 * It has a queue of tasks, each of which has a "frequency", nominally in units of Hz.
 * Tasks get a timestamp of when they were first queued. They are then serviced according
 * to a "figure of urgency", which is the time since queued, multiplied by the frequency.
 */
export class FairServiceScheduler {
    tasks = new Array();
    threads;
    errorSubj = new Subject();
    constructor() {
        this.threads = [this.makeThread()];
    }
    get isBusy() {
        return this.threads.some((t) => t.running);
    }
    get depth() {
        return this.tasks.length + this.threads.map((t) => t.running).reduce((a, b) => a + b);
    }
    get nThreads() {
        return this.threads.length;
    }
    set nThreads(n) {
        const toStop = this.threads.slice(n);
        this.threads = this.threads.slice(0, n);
        toStop.forEach((thread) => (thread.terminate = true));
        const newThreads = range(this.threads.length, n).map(this.makeThread);
        this.threads = [...this.threads, ...newThreads];
    }
    get errorObs() {
        return this.errorSubj.asObservable();
    }
    queue(exec, freq, desc) {
        return new Promise((resolve, reject) => {
            // internal times are in milliseconds, so internal freq is in kHz
            this.tasks.push(new FairServiceTask(exec, freq / 1000, resolve, reject, desc));
            const thread = this.threads.find((t) => !t.running);
            thread?.resolveIdle();
        });
    }
    async queueRetriable(p) {
        let attempts = 0;
        const translateError = p.errorTranslator ?? ((err) => err);
        for (;;) {
            try {
                return await this.queue(p.exec, attempts ? p.retryFreq : p.freq, p.desc);
            }
            catch (err) {
                ++attempts;
                const policy = p.fatalityChecker?.(err) ?? 'retry';
                if (policy !== 'failSilent') {
                    this.errorSubj.next(new FairServiceRetriableError(p.desc ? p.desc : '?', attempts, p.maxAttempts, err));
                }
                if (attempts >= p.maxAttempts || policy !== 'retry') {
                    throw translateError(err);
                }
            }
        }
    }
    makeThread = () => new FairServiceThread(this.tasks);
}
export class FairServiceRetriableError {
    desc;
    attempts;
    maxAttempts;
    err;
    constructor(desc, attempts, maxAttempts, err) {
        this.desc = desc;
        this.attempts = attempts;
        this.maxAttempts = maxAttempts;
        this.err = err;
    }
    get str() {
        return `${this.desc} failed, ${this.attempts}/${this.maxAttempts}`;
    }
}
class FairServiceTask {
    exec;
    freq;
    resolve;
    reject;
    desc;
    queued;
    constructor(exec, freq, resolve, reject, desc) {
        this.exec = exec;
        this.freq = freq;
        this.resolve = resolve;
        this.reject = reject;
        this.desc = desc;
        this.queued = monotonicTime();
    }
}
class FairServiceThread {
    tasks;
    terminate = false;
    idleResolver;
    constructor(tasks) {
        this.tasks = tasks;
        void this.loop();
    }
    get running() {
        return this.idleResolver ? 0 : 1;
    }
    async loop() {
        while (!this.terminate) {
            const now = monotonicTime();
            this.tasks.sort((a, b) => (now - a.queued) * a.freq - (now - b.queued) * b.freq);
            const task = this.tasks[this.tasks.length - 1];
            if (task) {
                this.tasks.pop();
                try {
                    task.resolve(await task.exec());
                }
                catch (err) {
                    task.reject(err);
                }
            }
            else {
                await new Promise((res) => (this.idleResolver = res));
            }
        }
    }
    resolveIdle = () => {
        if (this.idleResolver) {
            const resolveIdle = this.idleResolver;
            this.idleResolver = undefined;
            resolveIdle();
        }
    };
}
//# sourceMappingURL=sched.js.map